import type { MemoEntry } from "@/lib/types";
import { byPositionThenId } from "@/lib/positionOrder";

/**
 * Order a block's entries the way a DATED RUNNING LOG must read: chronologically.
 *
 * 🔴 WHY THIS IS STRUCTURAL AND NOT A CLAMP. `log_date` and `position` used to be two
 * independent orderings, and only `position` drove the render — so the date dividers were
 * whatever the positions happened to spell. `set_entry_log_date` changes the date and nothing
 * else, so re-dating an entry left it where it sat and merely relabelled its divider:
 * 27.04 → 29.06 → 20.04 on screen, oldest last. The insert path already had a clamp
 * (`resolve_insert_anchor`); `move_entry_to_block` and the position patch never got one.
 * A fourth clamp on re-date would have been the third guard on one invariant, and the next
 * write site would have forgotten it too. Ordering BY DATE makes every one of those paths
 * structurally incapable of breaking chronology — the same move that finally settled the
 * one-comment-one-Protokoll invariant.
 *
 * The rules, in order of precedence:
 *
 * 1. **A Zwischenabschnitt is a hard boundary.** A sub-heading (`kind === "subheading"`,
 *    `log_date === null`) groups the points beneath it positionally and resets the divider
 *    context. Points NEVER sort across one — an old-dated line under "COMPANY B" must not
 *    climb into "COMPANY A"'s group; it was never written there.
 *
 * 2. **An update travels with its to-do.** Updates (`kind === "update"`) are flat in the array,
 *    positioned after their parent and linked by `parent_entry_id`. They are sorted as part of
 *    their parent's cluster, never on their own date. Detaching one would render a colleague's
 *    board comment under somebody else's task, in the document the meeting reads from.
 *
 * 3. **Points sort by `log_date`, then by `position`.** The sort is stable, so a run of bullets
 *    typed in one meeting keeps its typing order; `position` is only the within-day tiebreak.
 *
 * 4. **Undated entries lead.** Entries written before migration 0037 carry `log_date = null` and
 *    render with no divider. They predate the dated log, so they sort first, keeping their
 *    relative order. (`""` sorts before any ISO date, and ISO dates sort lexicographically.)
 *
 * Idempotent: the three call sites (MemoBlockView ×2, memoFocusUnits) can each apply it without
 * agreeing on who runs first. If they disagreed, the presentation cursor would walk a different
 * document than the one on the shared screen.
 */
export function orderMemoLogEntries(entries: MemoEntry[]): MemoEntry[] {
  // Ties on `position` break by id, as the server reads them — never by arrival order.
  const byPosition = [...entries].sort(byPositionThenId);

  const out: MemoEntry[] = [];
  let segment: MemoEntry[] = [];

  const flushSegment = () => {
    if (!segment.length) return;
    out.push(...sortSegment(segment));
    segment = [];
  };

  for (const e of byPosition) {
    if (e.kind === "subheading") {
      flushSegment(); // rule 1: the heading closes the group above it
      out.push(e);
      continue;
    }
    segment.push(e);
  }
  flushSegment();

  return out;
}

/** One Zwischenabschnitt's worth of entries (no sub-headings), already in position order. */
function sortSegment(segment: MemoEntry[]): MemoEntry[] {
  // Rule 2: weld each update to the to-do it reports on, so the cluster moves as one unit.
  const clusters = new Map<string, MemoEntry[]>();
  const order: string[] = [];

  for (const e of segment) {
    if (e.kind === "update") {
      // Rule 2: weld to the to-do it reports on.
      if (e.parent_entry_id && clusters.has(e.parent_entry_id)) {
        clusters.get(e.parent_entry_id)!.push(e);
        continue;
      }
      // 🔴 ORPHAN: an update whose parent is not in this segment — reachable today, because
      // `move_entry_to_block` moves a to-do WITHOUT its updates, leaving them behind with a
      // cross-block `parent_entry_id`. It must be PINNED where it lies, welded to the cluster
      // above it. Giving it its own cluster head would sort it BY ITS OWN DATE into a stranger's
      // date group — rendering a colleague's board comment underneath somebody else's task, in
      // the document the meeting reads from. That is the precise failure rule 2 exists to
      // prevent, and it would have been reintroduced through the back door.
      const prev = order[order.length - 1];
      if (prev) {
        clusters.get(prev)!.push(e);
        continue;
      }
      // No cluster above it (a block that opens with an orphan update — the backend always
      // positions an update AFTER its to-do, so this is unreachable). Keep it rather than drop
      // it: misplacing a line is bad, losing it from the document is worse.
    }
    clusters.set(e.id, [e]);
    order.push(e.id);
  }

  // Rules 3 + 4: stable sort of the CLUSTER HEADS by date. Array.prototype.sort is stable, so
  // equal dates keep the position order they came in with.
  const head = (id: string) => clusters.get(id)![0]!;
  const dateKey = (e: MemoEntry) => e.log_date ?? "";

  return order
    .slice()
    .sort((a, b) =>
      dateKey(head(a)) < dateKey(head(b)) ? -1 : dateKey(head(a)) > dateKey(head(b)) ? 1 : 0,
    )
    .flatMap((id) => clusters.get(id)!);
}
